// Module 10 — the node-voltage applier: recolours the static grid-node point primitives from the
// engine's per-bus LinDistFlow voltage magnitude (busVMagPu). Phase 1.1: this is the "voltage overlay"
// channel — when toggled ON, node points show a green/yellow/orange/red ramp by live pu value instead
// of the default outage/status coloring. A blacked-out bus (pu ≤ 0) keeps the dead-grey so outage still
// reads. Mirrors node-assets.ts exactly (same colorCache + lastKey skip-cache discipline) so the
// 1,440-point recolor is free on a steady grid — only buses whose quantized band changes actually
// hit the Color setter. Boundary: same-module + domain only.
import { voltageBandStyle } from "./effects/palette";
import type { CesiumLike, PointPrimitiveLike, ColorLike } from "./cesium-api";

export interface NodeVoltageLayer {
  /** Recolor node points by live voltage magnitude. Called each tick when the overlay is ON. */
  apply(voltages: ReadonlyMap<string, number>): void;
  /** Clear the per-node cache so the next apply() repaints every node (called on toggle). */
  reset(): void;
}

export function makeNodeVoltageLayer(
  cesium: CesiumLike,
  index: ReadonlyMap<string, PointPrimitiveLike>,
): NodeVoltageLayer {
  const colorCache = new Map<string, ColorLike>(); // "hex|alpha" → parsed Color (reused across nodes)
  const lastKey = new Map<string, string>(); // node id → last quantized-voltage key (skip when unchanged)

  const colorFor = (key: string, hex: string, alpha: number): ColorLike => {
    let c = colorCache.get(key);
    if (!c) { c = cesium.Color.fromCssColorString(hex).withAlpha(alpha); colorCache.set(key, c); }
    return c;
  };

  return {
    apply(voltages): void {
      for (const [id, vMagPu] of voltages) {
        const pt = index.get(id);
        if (!pt) continue; // voltage map covers solver buses; some are anonymous junctions not rendered
        const s = voltageBandStyle(vMagPu);
        // Quantize the pu to 0.005 so a jittery 0.961/0.962 doesn't thrash the Color setter every tick.
        // The style ramp has 4 bands (green/yellow/orange/red + dead-grey), so the effective key is
        // already coarse — but the quantization protects against float noise WITHIN a band.
        const key = `${s.hex}|${s.alpha}|${Math.round(vMagPu * 200) / 200}`;
        if (lastKey.get(id) === key) continue;
        pt.color = colorFor(`${s.hex}|${s.alpha}`, s.hex, s.alpha);
        lastKey.set(id, key);
      }
    },

    reset(): void {
      lastKey.clear();
    },
  };
}
